/**
 * Auth context: wraps the app, exposes session/user + auth actions.
 *
 * Email/password only for now. Signup runs a domain pre-check (friendly
 * message) before hitting Supabase; the hard boundary is the Postgres
 * before-user-created hook (`check_domain_allowlist`), so a bypassed pre-check
 * still cannot create an unapproved account.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

export interface SignUpResult {
  error: string | null;
  needsConfirmation: boolean;
}

interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
  configured: boolean;
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<SignUpResult>;
  signOut: () => Promise<void>;
  isDomainApproved: (email: string) => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<{ error: string | null }>;
  updatePassword: (password: string) => Promise<{ error: string | null }>;
}

const DOMAIN_NOT_APPROVED =
  'O domínio do seu email não está autorizado. O registo é restrito a organizações aprovadas.';

async function isDomainApproved(email: string): Promise<boolean> {
  if (!isSupabaseConfigured) return false;
  const { data, error } = await supabase.rpc('is_email_domain_approved', {
    p_email: email,
  });
  if (error) return false;
  return Boolean(data);
}

async function signInWithPassword(email: string, password: string) {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  return { error: error?.message ?? null };
}

async function signUp(email: string, password: string, fullName?: string): Promise<SignUpResult> {
  // UI pre-check for a friendly message; the DB hook is the real gate.
  if (!(await isDomainApproved(email))) {
    return { error: DOMAIN_NOT_APPROVED, needsConfirmation: false };
  }
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: fullName ? { full_name: fullName } : undefined },
  });
  if (error) return { error: error.message, needsConfirmation: false };
  // With email confirmation on, `session` is null until the user confirms.
  return { error: null, needsConfirmation: !data.session };
}

async function signOut() {
  await supabase.auth.signOut();
}

/**
 * Send the branded PT recovery email. `redirectTo` is built from the running
 * origin so the same bundle works on dev.tutela.land, tutela.land and a local
 * dev server — each origin must be in the Supabase redirect allow-list
 * (`tools/supabase/setup.ts` ENV_URLS).
 *
 * Always resolves without error detail on success: we do NOT reveal whether an
 * address has an account (enumeration), the UI shows the same message either way.
 */
async function requestPasswordReset(email: string) {
  if (!isSupabaseConfigured) return { error: 'Autenticação indisponível.' };
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/redefinir`,
  });
  return { error: error?.message ?? null };
}

/** Set a new password for the CURRENT session (recovery session or logged-in user). */
async function updatePassword(password: string) {
  if (!isSupabaseConfigured) return { error: 'Autenticação indisponível.' };
  const { error } = await supabase.auth.updateUser({ password });
  return { error: error?.message ?? null };
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      configured: isSupabaseConfigured,
      signInWithPassword,
      signUp,
      signOut,
      isDomainApproved,
      requestPasswordReset,
      updatePassword,
    }),
    [session, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
